import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, Bot, CheckCircle2, TrendingUp, Star, Clock, 
  Target, FileText, Award, Zap, Layout, Search, BarChart, 
  Briefcase, Download, ArrowRight, PenTool, Radar, Database,
  Shield, Settings, Palette, PaintBucket, UserRound
} from 'lucide-react';

export function Features() {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header Section */}
      <section className="relative w-full py-20 overflow-hidden">
        {/* Fondos */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black" />
        
        {/* Efecto de resplandor */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-800/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-800/20 rounded-full blur-[80px] animate-pulse delay-300" />
        </div>
        
        {/* Contenido */}
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-6 border border-purple-900/30">
              <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
              <span className="text-sm font-medium">Características Avanzadas</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Potencia tu búsqueda de empleo con{' '}
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300 animate-pulse">
                  Curvia AI
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-300/20 blur-lg animate-pulse" />
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Descubre todas las funcionalidades que te ayudarán a destacar en el competitivo mercado laboral actual
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-950 via-purple-950/10 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Características principales
            </h2>
            <p className="text-xl text-gray-300">
              Todo lo que necesitas para crear un currículum profesional que destaque
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-purple-500/50 transition-all shadow-lg">
              <Bot className="w-12 h-12 text-purple-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Optimización con IA
              </h3>
              <p className="text-gray-400">
                Nuestra IA analiza tu CV y lo optimiza para destacar tus habilidades más relevantes para cada empleo
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-blue-500/50 transition-all shadow-lg">
              <Target className="w-12 h-12 text-blue-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Adaptación ATS
              </h3>
              <p className="text-gray-400">
                Optimiza tu CV para superar los sistemas de seguimiento de aplicaciones utilizados por empresas
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-yellow-500/50 transition-all shadow-lg">
              <Clock className="w-12 h-12 text-yellow-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Generación Rápida
              </h3>
              <p className="text-gray-400">
                Creamos tu CV optimizado en menos de 45 segundos, ahorrándote horas de trabajo manual
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-green-500/50 transition-all shadow-lg">
              <Layout className="w-12 h-12 text-green-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Múltiples Diseños
              </h3>
              <p className="text-gray-400">
                Elige entre más de 20 plantillas profesionales diseñadas por expertos en reclutamiento
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-red-500/50 transition-all shadow-lg">
              <BarChart className="w-12 h-12 text-red-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Análisis Detallado
              </h3>
              <p className="text-gray-400">
                Recibe una puntuación detallada de tu CV con sugerencias específicas para mejorarlo
              </p>
            </div>

            <div className="group bg-gray-900/60 backdrop-blur-sm p-8 rounded-xl border border-gray-800/80 hover:border-pink-500/50 transition-all shadow-lg">
              <Download className="w-12 h-12 text-pink-400 mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white mb-2">
                Exportación Flexible
              </h3>
              <p className="text-gray-400">
                Descarga tu CV en múltiples formatos como PDF, DOCX o imágenes listas para compartir
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Diseños de CV */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-950 via-purple-950/10 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-6 border border-purple-900/30">
              <Layout className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Diseños para todos los sectores</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Elige la plantilla ideal para tu profesión
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Más de 20 diseños profesionales para diferentes sectores y niveles de experiencia
            </p>
          </div>

          {/* Selector de Diseño Simple */}
          <div className="relative">
            {/* Carrusel de diseños */}
            <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory">
              {/* Plantilla 1 */}
              <div className="min-w-[280px] snap-center">
                <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-gray-200 transition-all hover:shadow-2xl">
                  {/* Vista previa */}
                  <div className="relative" style={{ aspectRatio: '1/1.414' }}>
                    {/* Simulación de CV - Diseño Moderno */}
                    <div className="w-1/3 absolute left-0 top-0 bottom-0 bg-gradient-to-b from-blue-600 to-blue-800"></div>
                    <div className="w-2/3 absolute right-0 top-0 bottom-0 bg-white">
                      <div className="absolute top-6 right-4 h-5 w-40 bg-blue-600 rounded"></div>
                      <div className="absolute top-14 right-4 h-3 w-20 bg-gray-300 rounded"></div>
                      <div className="absolute top-24 right-4 h-4 w-28 bg-blue-600 rounded"></div>
                      <div className="absolute top-32 right-4 h-2 w-36 bg-gray-300 rounded"></div>
                      <div className="absolute top-36 right-4 h-2 w-32 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                  {/* Etiqueta */}
                  <div className="p-3 border-t border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-800">Profesional Moderno</h3>
                    <p className="text-sm text-blue-600">Tecnología, Marketing</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-between">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm transition-colors">
                    Seleccionar
                  </button>
                  <div className="flex gap-1.5">
                    {['#3B82F6', '#10B981', '#F59E0B', '#6366F1'].map((color) => (
                      <button 
                        key={color}
                        className="w-6 h-6 rounded-full transition-transform hover:scale-110"
                        style={{ backgroundColor: color }}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Plantilla 2 */}
              <div className="min-w-[280px] snap-center">
                <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-gray-200 transition-all hover:shadow-2xl">
                  {/* Vista previa */}
                  <div className="relative" style={{ aspectRatio: '1/1.414' }}>
                    {/* Simulación de CV - Diseño Clásico */}
                    <div className="w-full h-24 absolute left-0 top-0 right-0 bg-gray-800"></div>
                    <div className="w-full absolute left-0 top-24 bottom-0 bg-white">
                      <div className="absolute top-6 left-1/2 transform -translate-x-1/2 h-5 w-48 bg-gray-800 rounded"></div>
                      <div className="absolute top-14 left-1/2 transform -translate-x-1/2 h-3 w-32 bg-gray-400 rounded"></div>
                      <div className="absolute top-24 left-4 right-4 h-px bg-gray-200"></div>
                      <div className="absolute top-32 left-4 h-4 w-32 bg-gray-800 rounded"></div>
                      <div className="absolute top-40 left-4 h-2 w-full max-w-[calc(100%-32px)] bg-gray-200 rounded"></div>
                      <div className="absolute top-44 left-4 h-2 w-full max-w-[calc(100%-32px)] bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  {/* Etiqueta */}
                  <div className="p-3 border-t border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-800">Ejecutivo Elegante</h3>
                    <p className="text-sm text-gray-600">Finanzas, Legal, Dirección</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-between">
                  <button className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-1.5 rounded text-sm transition-colors">
                    Seleccionar
                  </button>
                  <div className="flex gap-1.5">
                    {['#1F2937', '#0F766E', '#9333EA', '#BE123C'].map((color) => (
                      <button 
                        key={color}
                        className="w-6 h-6 rounded-full transition-transform hover:scale-110"
                        style={{ backgroundColor: color }}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Plantilla 3 */}
              <div className="min-w-[280px] snap-center">
                <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-gray-200 transition-all hover:shadow-2xl">
                  {/* Vista previa */}
                  <div className="relative" style={{ aspectRatio: '1/1.414' }}>
                    {/* Simulación de CV - Diseño Creativo */}
                    <div className="w-1/2 absolute left-0 top-0 bottom-0 bg-gradient-to-b from-purple-500 to-purple-700"></div>
                    <div className="w-1/2 absolute right-0 top-0 bottom-0 bg-white">
                      <div className="absolute top-10 right-4 h-5 w-32 bg-purple-600 rounded"></div>
                      <div className="absolute top-20 right-4 h-3 w-24 bg-purple-300 rounded"></div>
                      <div className="absolute top-30 right-4 h-4 w-28 bg-purple-600 rounded"></div>
                      <div className="absolute top-38 right-4 h-2 w-36 bg-gray-300 rounded"></div>
                      <div className="absolute top-42 right-4 h-2 w-32 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                  {/* Etiqueta */}
                  <div className="p-3 border-t border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-800">Creativo Innovador</h3>
                    <p className="text-sm text-purple-600">Diseño, Arte, Medios</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-between">
                  <button className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-sm transition-colors">
                    Seleccionar
                  </button>
                  <div className="flex gap-1.5">
                    {['#8B5CF6', '#EC4899', '#2563EB', '#0D9488'].map((color) => (
                      <button 
                        key={color}
                        className="w-6 h-6 rounded-full transition-transform hover:scale-110"
                        style={{ backgroundColor: color }}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Plantilla 4 */}
              <div className="min-w-[280px] snap-center">
                <div className="bg-white rounded-xl overflow-hidden shadow-xl border border-gray-200 transition-all hover:shadow-2xl">
                  {/* Vista previa */}
                  <div className="relative" style={{ aspectRatio: '1/1.414' }}>
                    {/* Simulación de CV - Diseño Minimalista */}
                    <div className="w-full p-8 h-full bg-white">
                      <div className="h-7 w-36 bg-green-600 mb-2"></div>
                      <div className="h-4 w-48 bg-green-200 mb-8"></div>
                      <div className="h-4 w-24 bg-green-600 mb-4"></div>
                      <div className="h-2 w-full bg-gray-200 mb-1"></div>
                      <div className="h-2 w-full bg-gray-200 mb-1"></div>
                      <div className="h-2 w-3/4 bg-gray-200 mb-6"></div>
                      <div className="h-4 w-24 bg-green-600 mb-4"></div>
                      <div className="h-2 w-full bg-gray-200 mb-1"></div>
                      <div className="h-2 w-full bg-gray-200 mb-1"></div>
                      <div className="h-2 w-3/4 bg-gray-200"></div>
                    </div>
                  </div>
                  {/* Etiqueta */}
                  <div className="p-3 border-t border-gray-200 bg-gray-50">
                    <h3 className="font-medium text-gray-800">Minimalista Simple</h3>
                    <p className="text-sm text-green-600">Académico, Investigación</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-between">
                  <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm transition-colors">
                    Seleccionar
                  </button>
                  <div className="flex gap-1.5">
                    {['#10B981', '#0369A1', '#4F46E5', '#B45309'].map((color) => (
                      <button 
                        key={color}
                        className="w-6 h-6 rounded-full transition-transform hover:scale-110"
                        style={{ backgroundColor: color }}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de navegación */}
            <button className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Características destacadas */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center">
              <div className="bg-purple-900/20 backdrop-blur-sm p-3 rounded-full mb-4">
                <Layout className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 text-center">Diseños Profesionales</h3>
              <p className="text-gray-400 text-center">Plantillas optimizadas para diferentes sectores y niveles profesionales</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-blue-900/20 backdrop-blur-sm p-3 rounded-full mb-4">
                <Palette className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 text-center">Personalización Total</h3>
              <p className="text-gray-400 text-center">Adapta colores, fuentes y estilos que mejor representen tu personalidad profesional</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-green-900/20 backdrop-blur-sm p-3 rounded-full mb-4">
                <Download className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2 text-center">Exportación Flexible</h3>
              <p className="text-gray-400 text-center">Descarga tu CV en PDF, DOCX o como imágenes de alta calidad para compartir</p>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Link 
              to="/dashboard"
              className="px-8 py-3.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(147,51,234,0.5)] inline-flex items-center gap-2"
            >
              <span>Diseña tu CV ahora</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Feature Banner 1 - AI Tech */}
      <section className="w-full py-16 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 to-gray-900">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/30 to-blue-500/30 rounded-lg blur-md"></div>
                <div className="relative bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-purple-500/30 shadow-xl">
                  <div className="aspect-video relative overflow-hidden rounded-lg bg-black/30 backdrop-blur-sm flex items-center justify-center">
                    <Bot className="w-24 h-24 text-purple-400 animate-pulse" />
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="w-full md:w-1/2">
              <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-4 border border-purple-900/30">
                <Bot className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Tecnología IA Avanzada</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Potencia tu CV con nuestra IA especializada
              </h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Análisis semántico para comprender el contexto laboral</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Identificación automática de palabras clave relevantes</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Reescritura inteligente de tus logros profesionales</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Adaptación dinámica al sector y puesto específico</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CV Designer Experience */}
      <section className="w-full py-20 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 to-gray-900">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-4 border border-purple-900/30">
              <Layout className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Experiencia de usuario real</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Diseña tu CV con total libertad creativa
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Personaliza colores, formatos y estilos para crear un CV que refleje tu personalidad profesional
            </p>
          </div>

          <div className="relative bg-gray-900/60 backdrop-blur-sm p-8 rounded-2xl border border-purple-500/30 shadow-xl overflow-hidden">
            {/* Efecto visual de fondo */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
            
            <div className="flex flex-col lg:flex-row gap-10 relative">
              {/* Panel de control lateral */}
              <div className="w-full lg:w-[280px] bg-gray-800/80 backdrop-blur-md rounded-xl p-5 border border-gray-700/50 shadow-lg lg:sticky lg:top-5 h-fit">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Palette className="w-5 h-5 mr-2 text-purple-400" />
                  Personalización
                </h3>
                
                {/* Selector de plantillas */}
                <div className="mb-6">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Diseño de CV</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((template) => (
                      <button
                        key={template}
                        className={`aspect-[3/4] rounded-lg overflow-hidden transition-all hover:scale-105 ${
                          template === 1 ? 'ring-2 ring-purple-500' : 'ring-1 ring-gray-700 hover:ring-purple-500/50'
                        }`}
                      >
                        <div className={`w-full h-full bg-gradient-to-br ${
                          template === 1 ? 'from-gray-900 to-gray-800' : 
                          template === 2 ? 'from-white to-gray-100' : 
                          'from-blue-900 to-purple-900'
                        }`}>
                          <div className={`h-1/4 ${template === 1 ? 'bg-gradient-to-r from-purple-600 to-blue-600' : template === 2 ? 'bg-gray-200' : 'bg-blue-800'}`}></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Selector de colores */}
                <div className="mb-6">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Colores</label>
                  <div className="flex flex-wrap gap-2">
                    {['#4B6A9B', '#2D6E5A', '#B8860B', '#DB7093', '#4B0082', '#8A2BE2'].map((color) => (
                      <button
                        key={color}
                        className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                          color === '#4B6A9B' ? 'ring-2 ring-white scale-110' : ''
                        }`}
                        style={{ backgroundColor: color }}
                        aria-label={`Color ${color}`}
                      />
                    ))}
                    <button className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-gray-300 transition-transform hover:scale-110">
                      <PaintBucket className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Configuración de fuentes */}
                <div className="mb-6">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Tipografía</label>
                  <select className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600">
                    <option>Serif profesional</option>
                    <option>Sans Serif moderno</option>
                    <option>Monospace técnico</option>
                  </select>
                </div>
                
                <div className="mb-2">
                  <label className="block text-gray-300 text-sm font-medium mb-2">Espaciado</label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">Compacto</span>
                    <input type="range" className="w-full accent-purple-500" min="1" max="5" value="3" />
                    <span className="text-xs text-gray-400">Amplio</span>
                  </div>
                </div>
                
                <button className="w-full mt-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium py-2 rounded-lg transition-colors">
                  Guardar cambios
                </button>
              </div>
              
              {/* Vista previa del CV */}
              <div className="flex-1 relative">
                {/* Botones de vista previa */}
                <div className="absolute -top-1 right-0 flex gap-2">
                  <button className="bg-gray-800/70 backdrop-blur-sm text-gray-300 hover:text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Descargar PDF
                  </button>
                  <button className="bg-gray-800/70 backdrop-blur-sm text-gray-300 hover:text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Vista previa
                  </button>
                </div>
                
                {/* CV Preview - Implementación real de un lado Sidebar + Contenido */}
                <div className="mt-7 bg-white rounded-lg overflow-hidden shadow-xl border border-gray-300 transition-all transform hover:shadow-2xl" style={{ aspectRatio: '1/1.414' }}>
                  {/* Simulación de un CV real con sidebar + contenido principal */}
                  <div className="flex h-full">
                    {/* Sidebar */}
                    <div className="w-1/3 h-full bg-gradient-to-b from-[#4B6A9B] to-[#364F6B] text-white">
                      {/* Foto */}
                      <div className="relative w-24 h-24 mx-auto mt-6 mb-4">
                        <div className="w-full h-full rounded-full" style={{ 
                          background: `linear-gradient(145deg, #4B6A9B80, #4B6A9B40)`,
                          padding: '3px',
                          boxShadow: `0 4px 12px #4B6A9B40`
                        }}>
                          <div className="w-full h-full rounded-full overflow-hidden border-2 border-white/30">
                            <div className="w-full h-full bg-black/20 flex items-center justify-center">
                              <UserRound className="w-12 h-12 text-white/50" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sobre Mí */}
                      <div className="px-4 py-2">
                        <h3 className="font-bold mb-2 text-sm">Sobre Mí</h3>
                        <div className="w-full h-2 bg-white/20 rounded-full mb-1"></div>
                        <div className="w-full h-2 bg-white/20 rounded-full mb-1"></div>
                        <div className="w-4/5 h-2 bg-white/20 rounded-full"></div>
                      </div>

                      {/* Contacto */}
                      <div className="px-4 py-2 mt-3">
                        <h3 className="font-bold mb-2 text-sm">Contacto</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-white/30"></div>
                            <div className="w-4/5 h-2 bg-white/20 rounded-full"></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-white/30"></div>
                            <div className="w-3/4 h-2 bg-white/20 rounded-full"></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-white/30"></div>
                            <div className="w-1/2 h-2 bg-white/20 rounded-full"></div>
                          </div>
                        </div>
                      </div>

                      {/* Habilidades */}
                      <div className="px-4 py-2 mt-3">
                        <h3 className="font-bold mb-2 text-sm">Habilidades</h3>
                        <div className="flex flex-wrap gap-1">
                          <div className="px-2 py-1 bg-white/20 rounded-md w-16 h-5"></div>
                          <div className="px-2 py-1 bg-white/20 rounded-md w-20 h-5"></div>
                          <div className="px-2 py-1 bg-white/20 rounded-md w-14 h-5"></div>
                          <div className="px-2 py-1 bg-white/20 rounded-md w-18 h-5"></div>
                          <div className="px-2 py-1 bg-white/20 rounded-md w-12 h-5"></div>
                        </div>
                      </div>

                      {/* Idiomas */}
                      <div className="px-4 py-2 mt-3">
                        <h3 className="font-bold mb-2 text-sm">Idiomas</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <div className="w-1/3 h-2 bg-white/20 rounded-full"></div>
                            <div className="w-1/4 h-2 bg-white/30 rounded-full"></div>
                          </div>
                          <div className="flex justify-between">
                            <div className="w-1/4 h-2 bg-white/20 rounded-full"></div>
                            <div className="w-1/4 h-2 bg-white/30 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contenido principal */}
                    <div className="w-2/3 h-full bg-white p-6">
                      {/* Encabezado */}
                      <div className="mb-4">
                        <div className="w-3/5 h-6 bg-gray-900 rounded mb-1"></div>
                        <div className="w-2/5 h-4 rounded" style={{ backgroundColor: '#4B6A9B' }}></div>
                      </div>

                      {/* Educación */}
                      <div className="mb-5">
                        <div className="w-1/3 h-5 rounded mb-3" style={{ backgroundColor: '#4B6A9B' }}></div>
                        
                        <div className="relative pl-8 mb-3">
                          <div 
                            className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2"
                            style={{ borderColor: '#4B6A9B', backgroundColor: 'white' }}
                          ></div>
                          <div className="w-4/5 h-3 bg-gray-800 rounded mb-1"></div>
                          <div className="w-3/5 h-2 rounded mb-1" style={{ backgroundColor: '#4B6A9B' }}></div>
                        </div>
                        
                        <div className="relative pl-8">
                          <div 
                            className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2"
                            style={{ borderColor: '#4B6A9B', backgroundColor: 'white' }}
                          ></div>
                          <div className="w-3/5 h-3 bg-gray-800 rounded mb-1"></div>
                          <div className="w-2/5 h-2 rounded" style={{ backgroundColor: '#4B6A9B' }}></div>
                        </div>
                      </div>

                      {/* Experiencia */}
                      <div>
                        <div className="w-2/5 h-5 rounded mb-3" style={{ backgroundColor: '#4B6A9B' }}></div>
                        
                        <div className="relative pl-8 mb-4">
                          <div 
                            className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2"
                            style={{ borderColor: '#4B6A9B', backgroundColor: 'white' }}
                          ></div>
                          <div className="w-full h-3 bg-gray-800 rounded mb-1"></div>
                          <div className="w-2/3 h-2 rounded mb-2" style={{ backgroundColor: '#4B6A9B' }}></div>
                          <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                          <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                          <div className="w-4/5 h-2 bg-gray-200 rounded"></div>
                        </div>
                        
                        <div className="relative pl-8">
                          <div 
                            className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2"
                            style={{ borderColor: '#4B6A9B', backgroundColor: 'white' }}
                          ></div>
                          <div className="w-4/5 h-3 bg-gray-800 rounded mb-1"></div>
                          <div className="w-3/5 h-2 rounded mb-2" style={{ backgroundColor: '#4B6A9B' }}></div>
                          <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                          <div className="w-4/5 h-2 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Puntos de interacción en vivo */}
                <div className="mt-5 bg-gray-800/70 backdrop-blur-sm rounded-lg p-4 border border-gray-700/50">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 bg-purple-900/30 rounded-full p-2.5">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Visualización interactiva en tiempo real</h4>
                      <p className="text-gray-400 text-sm">Prueba diferentes combinaciones de colores y diseños y ve los cambios al instante.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-10">
            <div className="max-w-3xl mx-auto bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-purple-500/20">
              <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-400" />
                Características premium
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <div className="flex gap-2 items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">20+ plantillas profesionales diseñadas por expertos en RRHH</p>
                </div>
                <div className="flex gap-2 items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">Paleta de colores ilimitada con recomendaciones personalizadas</p>
                </div>
                <div className="flex gap-2 items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300 text-sm">Múltiples opciones de exportación: PDF, DOCX, JPG y PNG</p>
                </div>
              </div>
              
              <div className="text-center">
                <Link 
                  to="/dashboard"
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg transition-all transform hover:scale-105 shadow-[0_0_15px_rgba(91,33,182,0.4)] hover:shadow-[0_0_25px_rgba(91,33,182,0.6)]"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <span>Prueba la personalización avanzada</span>
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Banner 2 - Design */}
      <section className="w-full py-16 relative overflow-hidden bg-gradient-to-br from-gray-900 to-purple-950/30">
        <div className="absolute inset-0">
          <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row-reverse items-center gap-12">
            <div className="w-full md:w-1/2">
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="aspect-[3/4] relative overflow-hidden rounded-lg bg-black/30 backdrop-blur-sm group">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 group-hover:from-purple-500/20 group-hover:to-blue-500/20 transition-all rounded-lg"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PenTool className="w-12 h-12 text-blue-400 group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="w-full md:w-1/2">
              <div className="inline-flex items-center px-4 py-2 bg-blue-900/20 backdrop-blur-sm rounded-full text-blue-400 mb-4 border border-blue-900/30">
                <Layout className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Diseños Profesionales</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Plantillas optimizadas para destacar tu perfil
              </h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Diseños adaptados a cada sector profesional</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Personalización de colores y estilos con un solo clic</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Formatos optimizados para impresión y lectura digital</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Tipografías profesionales seleccionadas por expertos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Templates Showcase */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-900 to-purple-950/30">
        <div className="absolute inset-0">
          <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-900/20 backdrop-blur-sm rounded-full text-blue-400 mb-4 border border-blue-900/30">
              <Layout className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Diseños Profesionales</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Elige entre múltiples plantillas premium
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Diseñadas por expertos en reclutamiento para maximizar tus oportunidades
            </p>
          </div>
          
          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Template 1 - Classic Professional */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/30 opacity-0 group-hover:opacity-100 transition-opacity z-10"></div>
              
              {/* Template Preview */}
              <div className="relative bg-white aspect-[3/4]">
                {/* Header */}
                <div className="h-24 bg-gradient-to-r from-blue-600 to-blue-700 p-4">
                  <div className="bg-white w-16 h-16 rounded-full absolute top-16 left-4 border-2 border-white"></div>
                </div>
                
                {/* Content */}
                <div className="pt-12 p-4">
                  <div className="w-3/4 h-4 bg-gray-800 rounded mb-2"></div>
                  <div className="w-1/2 h-3 bg-blue-600 rounded mb-4"></div>
                  
                  <div className="mb-4">
                    <div className="w-1/3 h-3 bg-gray-600 rounded mb-2"></div>
                    <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                    <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                    <div className="w-2/3 h-2 bg-gray-200 rounded"></div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="w-1/3 h-3 bg-gray-600 rounded mb-2"></div>
                    <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                    <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                    <div className="w-2/3 h-2 bg-gray-200 rounded"></div>
                  </div>
                  
                  <div>
                    <div className="w-1/3 h-3 bg-gray-600 rounded mb-2"></div>
                    <div className="flex gap-1 flex-wrap">
                      {[1, 2, 3, 4, 5].map((_, i) => (
                        <div key={i} className="bg-blue-100 w-12 h-5 rounded-full"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Template Name */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 z-10">
                <p className="text-white font-medium">Profesional Clásico</p>
                <div className="w-12 h-1 bg-blue-500 rounded mt-1"></div>
              </div>
              
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <span className="bg-white text-blue-600 px-4 py-2 rounded-full font-medium transform -translate-y-2 group-hover:translate-y-0 transition-transform">Usar esta plantilla</span>
              </div>
            </div>
            
            {/* Template 2 - Modern Creative */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-pink-600/30 opacity-0 group-hover:opacity-100 transition-opacity z-10"></div>
              
              {/* Template Preview */}
              <div className="relative bg-white aspect-[3/4]">
                {/* Header */}
                <div className="h-32 bg-gradient-to-r from-purple-500 to-purple-700 p-4 flex items-end">
                  <div>
                    <div className="w-32 h-4 bg-white rounded mb-1"></div>
                    <div className="w-24 h-3 bg-purple-300 rounded"></div>
                  </div>
                </div>
                
                {/* Content - Two Column Layout */}
                <div className="flex h-[calc(100%-8rem)]">
                  {/* Left Sidebar */}
                  <div className="w-1/3 bg-purple-50 p-3 space-y-4">
                    <div className="space-y-2">
                      <div className="w-full h-3 bg-purple-600 rounded"></div>
                      <div className="w-10 h-10 bg-purple-100 rounded-full mx-auto"></div>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="w-full h-2 bg-purple-600 rounded"></div>
                      <div className="w-full h-2 bg-gray-300 rounded"></div>
                      <div className="w-full h-2 bg-gray-300 rounded"></div>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="w-full h-2 bg-purple-600 rounded"></div>
                      <div className="w-full h-2 bg-gray-300 rounded"></div>
                      <div className="w-full h-2 bg-gray-300 rounded"></div>
                    </div>
                  </div>
                  
                  {/* Main Content */}
                  <div className="w-2/3 p-3 space-y-4">
                    <div className="space-y-1">
                      <div className="w-1/2 h-3 bg-gray-800 rounded"></div>
                      <div className="space-y-1">
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-3/4 h-2 bg-gray-400 rounded"></div>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="w-1/2 h-3 bg-gray-800 rounded"></div>
                      <div className="space-y-1">
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-full h-2 bg-gray-400 rounded"></div>
                        <div className="w-3/4 h-2 bg-gray-400 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Template Name */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 z-10">
                <p className="text-white font-medium">Creativo Moderno</p>
                <div className="w-12 h-1 bg-purple-500 rounded mt-1"></div>
              </div>
              
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <span className="bg-white text-purple-600 px-4 py-2 rounded-full font-medium transform -translate-y-2 group-hover:translate-y-0 transition-transform">Usar esta plantilla</span>
              </div>
            </div>
            
            {/* Template 3 - Executive Minimal */}
            <div className="group relative rounded-xl overflow-hidden shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 to-gray-900/30 opacity-0 group-hover:opacity-100 transition-opacity z-10"></div>
              
              {/* Template Preview */}
              <div className="relative bg-white aspect-[3/4]">
                {/* Header - Minimalist */}
                <div className="p-6 border-b-2 border-gray-200">
                  <div className="w-40 h-4 bg-gray-900 rounded mb-1"></div>
                  <div className="w-32 h-3 bg-gray-500 rounded"></div>
                </div>
                
                {/* Content */}
                <div className="p-4 space-y-4">
                  <div>
                    <div className="w-24 h-3 bg-gray-800 rounded mb-2"></div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
                      <div className="w-32 h-2 bg-gray-600 rounded"></div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
                      <div className="w-24 h-2 bg-gray-600 rounded"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="w-32 h-3 bg-gray-800 rounded mb-2"></div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
                      <div className="w-full h-2 bg-gray-600 rounded"></div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
                      <div className="w-full h-2 bg-gray-600 rounded"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
                      <div className="w-3/4 h-2 bg-gray-600 rounded"></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="w-20 h-3 bg-gray-800 rounded mb-2"></div>
                    <div className="flex flex-wrap gap-1">
                      {[1, 2, 3, 4, 5].map((_, i) => (
                        <div key={i} className="bg-gray-100 border border-gray-300 rounded w-14 h-6"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Template Name */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 z-10">
                <p className="text-white font-medium">Ejecutivo Minimalista</p>
                <div className="w-12 h-1 bg-gray-400 rounded mt-1"></div>
              </div>
              
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <span className="bg-white text-gray-800 px-4 py-2 rounded-full font-medium transform -translate-y-2 group-hover:translate-y-0 transition-transform">Usar esta plantilla</span>
              </div>
            </div>
          </div>
          
          {/* "See More" Button */}
          <div className="text-center mt-12">
            <Link 
              to="/dashboard"
              className="inline-flex items-center px-6 py-3 bg-blue-600/20 backdrop-blur-sm text-blue-400 rounded-lg border border-blue-600/30 hover:bg-blue-600/30 transition-colors"
            >
              <span className="mr-2">Ver más de 20 plantillas</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {/* Features List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mt-16 max-w-4xl mx-auto">
            <div className="flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
              <p className="text-gray-300">Diseños adaptados a cada sector profesional y nivel de experiencia</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
              <p className="text-gray-300">Personalización de colores y estilos con un solo clic</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
              <p className="text-gray-300">Formatos optimizados para impresión y visualización digital</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
              <p className="text-gray-300">Tipografías profesionales seleccionadas por expertos en RRHH</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Banner 3 - ATS Analysis */}
      <section className="w-full py-16 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950">
          <div className="absolute top-1/3 right-1/3 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="w-full md:w-1/2">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500/30 to-blue-500/30 rounded-lg blur-md"></div>
                <div className="relative bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-green-500/30 shadow-xl">
                  <div className="aspect-video relative overflow-hidden rounded-lg bg-black/30 backdrop-blur-sm flex items-center justify-center">
                    <Radar className="w-24 h-24 text-green-400 animate-pulse" />
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-blue-500/10 rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="w-full md:w-1/2">
              <div className="inline-flex items-center px-4 py-2 bg-green-900/20 backdrop-blur-sm rounded-full text-green-400 mb-4 border border-green-900/30">
                <Target className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Análisis ATS</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Supera los filtros automatizados de las empresas
              </h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Compatibilidad garantizada con sistemas ATS</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Puntuación de coincidencia con la oferta laboral</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Sugerencias en tiempo real para mejorar tu puntuación</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-gray-300">Algoritmo basado en datos de miles de contrataciones reales</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ATS Analysis Showcase */}
      <section className="w-full py-20 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950">
          <div className="absolute top-1/3 right-1/3 w-[400px] h-[400px] bg-green-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-green-900/20 backdrop-blur-sm rounded-full text-green-400 mb-4 border border-green-900/30">
              <Target className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Análisis ATS Inteligente</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Supera los filtros automatizados de contratación
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              El 75% de los CV nunca llegan a ser leídos por un humano. Asegura que el tuyo pase los filtros ATS.
            </p>
          </div>
          
          <div className="bg-gray-900/60 backdrop-blur-sm p-6 lg:p-10 rounded-2xl border border-green-500/30 shadow-xl mb-12">
            <div className="flex flex-col lg:flex-row gap-8 items-stretch">
              {/* ATS Analysis Dashboard */}
              <div className="w-full lg:w-2/3 relative">
                <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
                  {/* Dashboard Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-white">Análisis de compatibilidad ATS</h3>
                      <p className="text-green-400">Tu CV vs. Oferta: Desarrollador Full Stack</p>
                    </div>
                    <div className="bg-gray-700 rounded-lg px-4 py-2 text-white font-bold flex items-center">
                      <span className="text-green-400 mr-1">87</span>/100
                    </div>
                  </div>
                  
                  {/* Analysis Sections */}
                  <div className="space-y-6">
                    {/* Keywords Match */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-white font-medium">Palabras clave relevantes</h4>
                        <span className="text-green-400 font-bold">92%</span>
                      </div>
                      <div className="h-2 bg-gray-600 rounded-full overflow-hidden mb-4">
                        <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded text-xs">React.js ✓</span>
                        <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded text-xs">Node.js ✓</span>
                        <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded text-xs">TypeScript ✓</span>
                        <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded text-xs">MongoDB ✓</span>
                        <span className="bg-red-900/30 text-red-400 px-2 py-1 rounded text-xs">AWS ✗</span>
                        <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded text-xs">Git ✓</span>
                      </div>
                    </div>
                    
                    {/* Format & Structure */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-white font-medium">Formato y estructura</h4>
                        <span className="text-green-400 font-bold">100%</span>
                      </div>
                      <div className="h-2 bg-gray-600 rounded-full overflow-hidden mb-4">
                        <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{ width: '100%' }}></div>
                      </div>
                      <div className="text-gray-300 text-sm flex gap-4">
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span>Legible por ATS</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span>Sin tablas complejas</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                          <span>Fuentes compatibles</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Experience Match */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-white font-medium">Experiencia relevante</h4>
                        <span className="text-yellow-400 font-bold">78%</span>
                      </div>
                      <div className="h-2 bg-gray-600 rounded-full overflow-hidden mb-4">
                        <div className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                      <p className="text-yellow-400 text-sm flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        <span>Sugerencia: Enfatiza tu experiencia en desarrollo front-end con React</span>
                      </p>
                    </div>
                    
                    {/* Achievements */}
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-white font-medium">Logros cuantificados</h4>
                        <span className="text-green-400 font-bold">90%</span>
                      </div>
                      <div className="h-2 bg-gray-600 rounded-full overflow-hidden mb-4">
                        <div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                      <p className="text-white text-sm">Excelente uso de métricas y resultados específicos en tus logros profesionales.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Benefits Column */}
              <div className="w-full lg:w-1/3">
                <div className="bg-green-900/10 backdrop-blur-sm border border-green-500/20 rounded-xl h-full p-6 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4">¿Por qué es importante?</h3>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex gap-3">
                        <div className="w-10 h-10 bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <Star className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <h4 className="text-white font-medium">Aumenta tus posibilidades</h4>
                          <p className="text-gray-400 text-sm">Un CV optimizado para ATS tiene 7 veces más probabilidades de ser seleccionado.</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="w-10 h-10 bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <Search className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <h4 className="text-white font-medium">Palabras clave precisas</h4>
                          <p className="text-gray-400 text-sm">Nuestra IA identifica y sugiere las palabras clave exactas que buscan los reclutadores.</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-3">
                        <div className="w-10 h-10 bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <Zap className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <h4 className="text-white font-medium">Recomendaciones en vivo</h4>
                          <p className="text-gray-400 text-sm">Recibe sugerencias en tiempo real mientras editas tu currículum.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/60 rounded-lg p-4 border border-green-500/20">
                    <p className="text-gray-300 text-sm mb-3">
                      <span className="text-green-400 font-bold">93%</span> de las empresas utilizan sistemas ATS para filtrar candidatos.
                    </p>
                    <div className="flex items-center gap-2">
                      <img src="https://brandlogos.net/wp-content/uploads/2022/08/workday-logo-brandlogo.net_.png" alt="Workday" className="h-4 bg-white p-0.5 rounded" />
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Greenhouse_Software_Logo.svg/1200px-Greenhouse_Software_Logo.svg.png" alt="Greenhouse" className="h-4 bg-white p-0.5 rounded" />
                      <img src="https://asset.brandfetch.io/idCfnT_nUj/iddV75F4BZ.png" alt="Lever" className="h-4 bg-white p-0.5 rounded" />
                      <p className="text-xs text-gray-500">y más sistemas ATS</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Link 
              to="/dashboard"
              className="inline-flex items-center px-6 py-3 bg-green-600/20 backdrop-blur-sm text-green-400 rounded-lg border border-green-600/30 hover:bg-green-600/30 transition-colors"
            >
              <span className="mr-2">Analiza tu CV ahora</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-gray-950 via-purple-950/10 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Por qué elegir Curvia AI?
            </h2>
            <p className="text-xl text-gray-300">
              Comparativa entre Curvia AI y otros creadores de CV tradicionales
            </p>
          </div>

          <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-purple-500/30 shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="p-4 text-left text-gray-400">Características</th>
                    <th className="p-4 text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300">Curvia AI</th>
                    <th className="p-4 text-center text-gray-400">Otros Creadores de CV</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-800/30">
                    <td className="p-4 text-white">Optimización con IA</td>
                    <td className="p-4 text-center"><CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" /></td>
                    <td className="p-4 text-center"><FileText className="w-5 h-5 text-red-400 mx-auto" /></td>
                  </tr>
                  <tr className="border-t border-gray-800/30">
                    <td className="p-4 text-white">Análisis ATS</td>
                    <td className="p-4 text-center"><CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" /></td>
                    <td className="p-4 text-center"><FileText className="w-5 h-5 text-red-400 mx-auto" /></td>
                  </tr>
                  <tr className="border-t border-gray-800/30">
                    <td className="p-4 text-white">Adaptación por puesto</td>
                    <td className="p-4 text-center"><CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" /></td>
                    <td className="p-4 text-center"><FileText className="w-5 h-5 text-red-400 mx-auto" /></td>
                  </tr>
                  <tr className="border-t border-gray-800/30">
                    <td className="p-4 text-white">Tiempo de creación</td>
                    <td className="p-4 text-center text-green-400">45 segundos</td>
                    <td className="p-4 text-center text-red-400">Horas o días</td>
                  </tr>
                  <tr className="border-t border-gray-800/30">
                    <td className="p-4 text-white">Sugerencias personalizadas</td>
                    <td className="p-4 text-center"><CheckCircle2 className="w-5 h-5 text-green-400 mx-auto" /></td>
                    <td className="p-4 text-center"><FileText className="w-5 h-5 text-red-400 mx-auto" /></td>
                  </tr>
                  <tr className="border-t border-gray-800/30">
                    <td className="p-4 text-white">Tasa de éxito en entrevistas</td>
                    <td className="p-4 text-center text-green-400">7x mayor</td>
                    <td className="p-4 text-center text-red-400">Estándar</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="w-full py-16 relative overflow-hidden bg-gray-950">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950 to-gray-900">
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-blue-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">
              Tecnología de punta
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nuestras soluciones técnicas que hacen posible un CV excepcional
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-gray-800/80 hover:border-blue-500/50 transition-all">
              <Zap className="w-10 h-10 text-blue-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Procesamiento ultrarrápido</h3>
              <p className="text-gray-400 text-sm">Algoritmos optimizados para generar resultados en segundos</p>
            </div>
            
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-gray-800/80 hover:border-purple-500/50 transition-all">
              <Database className="w-10 h-10 text-purple-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Base de datos avanzada</h3>
              <p className="text-gray-400 text-sm">Entrenada con millones de CVs exitosos en todas las industrias</p>
            </div>
            
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-gray-800/80 hover:border-green-500/50 transition-all">
              <Shield className="w-10 h-10 text-green-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Seguridad garantizada</h3>
              <p className="text-gray-400 text-sm">Protección de datos con encriptación de nivel bancario</p>
            </div>
            
            <div className="bg-gray-900/60 backdrop-blur-sm p-6 rounded-xl border border-gray-800/80 hover:border-yellow-500/50 transition-all">
              <Settings className="w-10 h-10 text-yellow-400 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Personalización total</h3>
              <p className="text-gray-400 text-sm">Ajustes precisos para adaptarse a tus necesidades específicas</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="w-full py-20 relative overflow-hidden bg-gradient-to-br from-purple-950 to-gray-900">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-gray-900/60 backdrop-blur-sm p-10 rounded-xl border border-purple-500/30 shadow-xl max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Crea tu CV optimizado hoy mismo
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              No pierdas más oportunidades laborales. Potencia tu búsqueda de empleo con un CV profesional creado con IA
            </p>
            <Link 
              to="/dashboard"
              className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg transition-all transform hover:scale-105 hover:shadow-[0_0_30px_rgba(147,51,234,0.7)]"
            >
              <span className="relative z-10 flex items-center gap-2">
                <span>Crear mi CV optimizado</span>
                <ArrowRight className="w-5 h-5" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
} 